export type HexColor = `#${string}`;

export interface AppearancePreset {
  readonly id: string;
  readonly name: string;
  readonly color: HexColor;
  readonly swatchBackground: string;
}

export const DEFAULT_APPEARANCE_COLOR: HexColor = '#0096B4';

function swatchBackground(base: string, glow: string): string {
  return (
    `radial-gradient(40px 40px at 0 0, hsl(${glow} / 1) 0%, hsl(${glow} / 0) 100%), ` +
    `linear-gradient(hsl(${base}), hsl(${base}))`
  );
}

export const APPEARANCE_PRESETS = [
  {
    id: 'blue',
    name: 'Синий',
    color: '#000399',
    swatchBackground: swatchBackground('240 93% 5%', '239 100% 30%'),
  },
  {
    id: 'orange',
    name: 'Оранжевый',
    color: '#802F00',
    swatchBackground: swatchBackground('22 100% 5%', '22 100% 25%'),
  },
  {
    id: 'green',
    name: 'Зелёный',
    color: '#1A6659',
    swatchBackground: swatchBackground('172 65% 5%', '170 60% 25%'),
  },
  {
    id: 'teal',
    name: 'Бирюзовый',
    color: DEFAULT_APPEARANCE_COLOR,
    swatchBackground: swatchBackground('190 100% 5%', '190 100% 25%'),
  },
  {
    id: 'violet',
    name: 'Фиолетовый',
    color: '#482659',
    swatchBackground: swatchBackground('280 40% 5%', '280 40% 25%'),
  },
  {
    id: 'minimal',
    name: 'Минимализм',
    color: '#000000',
    swatchBackground: swatchBackground('0 0% 0%', '0 0% 0%'),
  },
] as const satisfies readonly AppearancePreset[];

const PRESET_PALETTES = {
  blue: { background: '#010119', ambientRgb: '0 2.55 153' },
  orange: { background: '#1A0900', ambientRgb: '127.5 46.75 0' },
  green: { background: '#041513', ambientRgb: '25.5 102 89.25' },
  teal: { background: '#001114', ambientRgb: '0 76.5 91.8' },
  violet: { background: '#0E0812', ambientRgb: '72.25 38.25 89.25' },
  minimal: { background: '#000000', ambientRgb: '0 0 0' },
} as const;

export interface AppearanceColors {
  readonly color: HexColor;
  readonly background: HexColor;
  readonly accent: HexColor;
  /** Space-separated RGB channels for `rgb(var(--ambient-rgb) / opacity)`. */
  readonly ambientRgb: string;
  readonly cssVariables: Readonly<{
    '--bg': HexColor;
    '--accent': HexColor;
    '--ambient-rgb': string;
  }>;
}

/** Accept a full CSS hex color, trimming whitespace and normalizing its case. */
export function normalizeHexColor(value: unknown): HexColor | null {
  if (typeof value !== 'string') return null;
  const color = value.trim().toUpperCase();
  return /^#[0-9A-F]{6}$/.test(color) ? (color as HexColor) : null;
}

type Rgb = readonly [number, number, number];

function toHsl(color: HexColor): readonly [number, number, number] {
  const channel = (offset: number) => Number.parseInt(color.slice(offset, offset + 2), 16) / 255;
  const red = channel(1);
  const green = channel(3);
  const blue = channel(5);
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  const lightness = (max + min) / 2;
  if (delta === 0) return [0, 0, lightness];

  const saturation = delta / (1 - Math.abs(2 * lightness - 1));
  const hue =
    max === red
      ? ((green - blue) / delta) % 6
      : max === green
        ? (blue - red) / delta + 2
        : (red - green) / delta + 4;
  return [(hue * 60 + 360) % 360, saturation, lightness];
}

function toRgb(hue: number, saturation: number, lightness: number): Rgb {
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const intermediate = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const minimum = lightness - chroma / 2;
  const channels: Rgb =
    hue < 60
      ? [chroma, intermediate, 0]
      : hue < 120
        ? [intermediate, chroma, 0]
        : hue < 180
          ? [0, chroma, intermediate]
          : hue < 240
            ? [0, intermediate, chroma]
            : hue < 300
              ? [intermediate, 0, chroma]
              : [chroma, 0, intermediate];
  return [
    (channels[0] + minimum) * 255,
    (channels[1] + minimum) * 255,
    (channels[2] + minimum) * 255,
  ];
}

function toHex(channels: Rgb): HexColor {
  return `#${channels
    .map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

/** Keep user-selected hues within the app's dark background and readable accents. */
export function deriveAppearanceColors(value: string): AppearanceColors {
  const color = normalizeHexColor(value);
  if (!color) throw new RangeError('Цвет должен быть в формате #RRGGBB');

  const preset = APPEARANCE_PRESETS.find((preset) => preset.color === color);
  const sourcePalette = preset ? PRESET_PALETTES[preset.id] : undefined;
  const [hue, saturation, sourceLightness] = toHsl(color);
  const background =
    sourcePalette?.background ??
    toHex(toRgb(hue, saturation, Math.min(20 / 510, sourceLightness / 4)));
  // Background changes retain the source application's blue links and focus states.
  const accent = '#6fa0ff';
  const ambientRgb =
    sourcePalette?.ambientRgb ??
    toRgb(hue, saturation, Math.min(0.18, sourceLightness))
      .map((channel) => String(Math.round(channel * 100) / 100))
      .join(' ');

  return {
    color,
    background,
    accent,
    ambientRgb,
    cssVariables: { '--bg': background, '--accent': accent, '--ambient-rgb': ambientRgb },
  };
}
